import { Table, TableBody, TableRow, Tooltip, Typography } from '@mui/material';
import type { InventoryResource } from '@repo/api-gw-sdk';
import { Roboto_Mono } from 'next/font/google';

import { FileLink, isWindowsMachine } from '@/components/explorer/fileLink';
import { Loader } from '@/components/layout/loading';
import { BorderlessTableCell } from '@/components/table/components';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';

import { AutoClassification } from './autoClassifcationButtons';

const roboto = Roboto_Mono({
  weight: '600',
  subsets: ['latin'],
});

export function SensitivityAnnotationsViewer({
  entity,
  setEditorOpen,
  restartDetection,
}: {
  entity: InventoryResource;
  setEditorOpen: () => void;
  restartDetection: () => void;
}) {
  const { isAuthorizedResource } = useRoles();
  const dal = useDAL();

  const { data: body } = dal.inventory.sensitivityAnnotations.useList(
    entity.id
  );

  if (!body) {
    return <Loader />;
  }

  const annotations = body.sensitivityAnnotations;

  if (entity.classifications?.sensitivityAnnotationsDetails?.isOverridden) {
    return (
      <>
        <AutoClassification
          testId='sensitivity-annotations-auto-classification-buttons-override'
          textOn=''
          isAutoClassificationOn={false}
          restartDetectionClicked={() => restartDetection()}
          editOverridesClicked={() => setEditorOpen()}
          setEditorOpen={() => setEditorOpen()}
          disabled={!isAuthorizedResource('update:data_classification', entity)}
        />
        <Typography component='span'>
          Set manually to{' '}
          <Typography component='span' fontWeight={500}>
            {entity.classifications.sensitivityAnnotationsDetails
              .hasSensitivityAnnotations
              ? 'Detected'
              : 'Not detected'}
          </Typography>
        </Typography>
      </>
    );
  }

  const textOn = annotations.length
    ? 'Sensitivity labels were detected. Here are a few samples:'
    : 'No sensitivity labels were detected';

  const isWindows = isWindowsMachine(
    annotations.map((x) => x.filePath).filter(Boolean)
  );

  return (
    <>
      <AutoClassification
        testId='sensitivity-annotations-auto-classification-buttons-auto'
        textOn={textOn}
        isAutoClassificationOn={true}
        restartDetectionClicked={() => restartDetection()}
        editOverridesClicked={() => setEditorOpen()}
        setEditorOpen={() => setEditorOpen()}
        disabled={!isAuthorizedResource('update:data_classification', entity)}
      />
      {!!annotations.length && (
        <Table>
          <TableBody>
            {annotations.map((annotation, index) => (
              <TableRow key={index} sx={{ height: '56px' }}>
                <Tooltip title={annotation.propertyName}>
                  <BorderlessTableCell className='font-semibold w-3/12'>
                    {annotation.propertyValue}
                  </BorderlessTableCell>
                </Tooltip>
                <BorderlessTableCell className={roboto.className}>
                  {typeof annotation.filePath === 'string' && (
                    <FileLink
                      entity={entity}
                      location={annotation.filePath}
                      isWindowsMachine={isWindows}
                    />
                  )}
                </BorderlessTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}
